// neo_cogym — 콘텐츠 패널 (W6, P1 모듈 사용)
// PDF 교재(페이지 넘김) + 동영상 강의(HLS)를 콘텐츠 타입에 따라 렌더. 진도 자동 보고.

import { useEffect, useRef, useState } from "react";
import { createPdfViewer, type PdfViewerHandle } from "../content/pdfViewer";
import { attachVideoPlayer } from "../content/videoPlayer";
import { createProgressReporter } from "../content/progress";
import type { ContentAsset } from "../content/types";

const reporter = createProgressReporter();

export function ContentPane({ asset }: { asset: ContentAsset | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pdfRef = useRef<PdfViewerHandle | null>(null);
  const [page, setPage] = useState({ cur: 1, total: 1 });

  // PDF
  useEffect(() => {
    if (!asset || asset.type !== "PDF" || !canvasRef.current) return;
    let handle: PdfViewerHandle | null = null;
    let alive = true;
    createPdfViewer({
      url: asset.src,
      canvas: canvasRef.current,
      contentAssetId: asset.id,
      onProgress: (p) => {
        reporter.report(p);
        if (p.lastPage) setPage((s) => ({ ...s, cur: p.lastPage! }));
      },
    }).then((h) => {
      if (!alive) return h.destroy();
      handle = h;
      pdfRef.current = h;
      setPage({ cur: h.current(), total: h.pageCount });
    });
    return () => {
      alive = false;
      handle?.destroy();
      pdfRef.current = null;
    };
  }, [asset]);

  // VIDEO
  useEffect(() => {
    if (!asset || asset.type !== "VIDEO" || !videoRef.current) return;
    let detach: (() => void) | null = null;
    attachVideoPlayer({
      src: asset.src,
      video: videoRef.current,
      contentAssetId: asset.id,
      durationSec: asset.meta.durationSec,
      captions: asset.meta.captions,
      onProgress: (p) => reporter.report(p),
    }).then((d) => (detach = d));
    return () => detach?.();
  }, [asset]);

  if (!asset) return <div className="content-empty">콘텐츠를 선택하세요</div>;

  if (asset.type === "VIDEO") {
    return (
      <div className="content-pane">
        <video ref={videoRef} controls playsInline className="content-video" />
      </div>
    );
  }
  return (
    <div className="content-pane">
      <div className="pdf-canvas-wrap">
        <canvas ref={canvasRef} className="pdf-canvas" />
      </div>
      <div className="pdf-nav">
        <button onClick={() => pdfRef.current?.prev()} aria-label="이전 페이지">◀</button>
        <span>{page.cur} / {page.total}</span>
        <button onClick={() => pdfRef.current?.next()} aria-label="다음 페이지">▶</button>
      </div>
    </div>
  );
}
